import { motion } from "framer-motion";
import {
  Recycle,
  TrendingUp,
  Users,
  MonitorSmartphone,
  BarChart3,
  Lock,
} from "lucide-react";
import type { JSX } from "react";

type Feature = {
  icon: JSX.Element;
  title: string;
  desc: string;
};

export default function Choosing() {
  const features: Feature[] = [
    {
      icon: <TrendingUp className="w-8 h-8 text-teal-700" />,
      title: "Grow Your Revenue",
      desc: "Increase your sales by offering food, groceries, everyday essentials, and Mystery Bags through one powerful platform.",
    },
    {
      icon: <Recycle className="w-8 h-8 text-teal-700" />,
      title: "Reduce Food Waste",
      desc: "Turn surplus food into additional revenue with Mystery Bags while supporting a more sustainable future.",
    },
    {
      icon: <Users className="w-8 h-8 text-teal-700" />,
      title: "Reach More Customers",
      desc: "Connect with thousands of local customers looking for restaurants, supermarkets, groceries, and great deals.",
    },
    {
      icon: <MonitorSmartphone className="w-8 h-8 text-teal-700" />,
      title: "Easy Business Management",
      desc: "Manage products, orders, delivery, pickup, and Mystery Bags effortlessly from a single dashboard.",
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-teal-700" />,
      title: "Business Insights",
      desc: "Track orders, monitor sales performance, and gain valuable insights to help your business grow.",
    },
    {
      icon: <Lock className="w-8 h-8 text-teal-700" />,
      title: "Complete Control",
      desc: "Set your business hours, manage inventory, control order availability, and update products anytime.",
    },
  ];

  return (
    <section
      id="choose"
      className="w-full bg-linear-to-b from-[#f5f5dc] to-white py-16 px-6 md:px-12 lg:px-20"
    >
      <div className="text-center mb-12">
        <motion.h2
          className="text-3xl md:text-4xl font-semibold text-gray-900"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          Why Vendors Choose{" "}
          <span className="text-orange-500 font-bold">SMARTLY ORDERS</span>
        </motion.h2>

        <motion.p
          className="text-gray-700 mt-3 text-lg"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Grow your business, reach more customers, and manage everything from
          one platform.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 max-w-6xl mx-auto">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            className="w-full flex flex-col items-center text-center p-8 bg-white border border-teal-100 rounded-xl shadow-sm hover:shadow-lg hover:border-teal-300 transition-all duration-300 hover:-translate-y-1"
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              ease: "easeOut",
              delay: index * 0.15,
            }}
            viewport={{ once: true }}
          >
            <motion.div
              className="mb-4 p-3 bg-teal-50 rounded-full"
              whileHover={{ rotate: 10, scale: 1.1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              {feature.icon}
            </motion.div>

            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {feature.title}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {feature.desc}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
